import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cloud, Loader2, Clock, CheckCircle2, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const KIND_LABEL = {
  cliente_folders: "Creazione cartelle",
  cliente_sync: "Sincronizzazione file",
  commessa_folder: "Cartella commessa",
};

export default function ActiveJobsBadge() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const [stats, setStats] = useState({ queued: 0, running: 0, workers: 0 });
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const pollRef = useRef(null);
  const dropRef = useRef(null);

  const fetchJobs = async () => {
    try {
      const { data } = await api.get("/nextcloud/jobs/active");
      setCount(data.count || 0);
      setStats({ queued: data.queued || 0, running: data.running || 0, workers: data.workers || 0 });
      setJobs(data.jobs || []);
    } catch {
      // silently ignore (session expired / offline)
    }
  };

  useEffect(() => {
    fetchJobs();
    pollRef.current = setInterval(fetchJobs, 2000);
    // Refresh on demand when a job is fired from another component
    const onJobFired = () => fetchJobs();
    window.addEventListener("nurel:job-fired", onJobFired);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener("nurel:job-fired", onJobFired);
    };
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (count === 0) return null;

  return (
    <div className="relative" ref={dropRef} data-testid="active-jobs-badge">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
        style={{ background: "#FFF8E1", color: "#7A4500", border: "1px solid #F2B900" }}
        data-testid="active-jobs-btn"
        title={`Aruba Drive: ${stats.running} in corso, ${stats.queued} in coda · ${stats.workers} worker`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>
          {stats.running > 0 && `${stats.running} attivi`}
          {stats.running > 0 && stats.queued > 0 && " · "}
          {stats.queued > 0 && `${stats.queued} in coda`}
        </span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 card-shell z-50 overflow-hidden max-md:fixed max-md:left-2 max-md:right-2 max-md:top-16 max-md:mt-0 max-md:w-auto"
          style={{ background: "var(--surface)", boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}
          data-testid="active-jobs-dropdown"
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2 mb-1">
              <Cloud className="w-4 h-4" style={{ color: "var(--primary)" }} />
              <div className="text-sm font-bold" style={{ fontFamily: "Manrope" }}>Aruba Drive — operazioni</div>
            </div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              {stats.running} in corso · {stats.queued} in coda · {stats.workers} worker disponibili
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {jobs.map((j) => (
              <Link
                key={j.id}
                to={j.link || "#"}
                onClick={() => setOpen(false)}
                className="block px-4 py-3 border-b hover:bg-black/5 transition-colors"
                style={{ borderColor: "var(--border)" }}
                data-testid={`active-job-${j.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {j.status === "queued" ? (
                    <Clock className="w-3.5 h-3.5" style={{ color: "#7A4500" }} />
                  ) : (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--primary)" }} />
                  )}
                  <span className="text-xs font-semibold">{KIND_LABEL[j.kind] || j.kind}</span>
                  <span className="ml-auto text-[10px]" style={{ color: "var(--text-muted)" }}>
                    {j.status === "queued" ? "in coda" : "in corso"}
                  </span>
                </div>
                <div className="text-sm truncate" style={{ color: "var(--text)" }}>{j.label}</div>
                {j.total > 0 && (
                  <div className="mt-1 h-1 rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.08)" }}>
                    <div
                      className="h-full transition-all"
                      style={{ width: `${Math.min(100, (j.progress / j.total) * 100)}%`, background: "var(--primary)" }}
                    />
                  </div>
                )}
                {j.total > 0 && (
                  <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                    {j.progress} / {j.total}
                  </div>
                )}
              </Link>
            ))}
          </div>
          <div className="px-4 py-2 text-[11px] text-center" style={{ background: "#E8F3EB", color: "#1E4620" }}>
            <CheckCircle2 className="inline w-3 h-3 mr-1" /> Puoi continuare a lavorare — il sistema ti avviserà
          </div>
          {user?.role === "admin" && (
            <Link
              to="/aruba-jobs"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-xs font-semibold text-center border-t hover:bg-black/5 transition-colors"
              style={{ borderColor: "var(--border)", color: "var(--primary)" }}
              data-testid="badge-link-aruba-jobs"
            >
              <ExternalLink className="inline w-3 h-3 mr-1" /> Vedi storico job e retry
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
